import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from '@/utils/axiosConfig';
import { useAuth } from './AuthContext';
import { getApiUrl } from '@/utils/apiUrl';

const PermissionsContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  
  const API = getApiUrl();

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!token || !user) {
        setPermissions({});
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/users/my-permissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPermissions(response.data.permissions || {});
      } catch (error) {
        setPermissions({});
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [user, token, API]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasPermission = useCallback((permission) => {
    return permissions[permission] === true;
  }, [permissions]);

  const isSuperAdmin = useCallback(() => {
    return user?.is_super_admin === true;
  }, [user]);

  const hasRole = useCallback((role) => {
    return user?.employee_roles?.includes(role) || false;
  }, [user]);

  const ctxValue = useMemo(() => ({
    permissions, hasPermission, isSuperAdmin, hasRole, loading
  }), [permissions, hasPermission, isSuperAdmin, hasRole, loading]);

  return (
    <PermissionsContext.Provider value={ctxValue}>
      {children}
    </PermissionsContext.Provider>
  );
};
